/* eslint-disable @typescript-eslint/no-explicit-any */
import { Obj } from '#/utils'
import router from '@/router'
import {
  LocationQueryRaw,
  RouteLocationNormalized,
  RouteRecordName,
  RouteRecordNormalized
} from 'vue-router'

type TargetContext = '_self' | '_blank'

export function openWindow(
  url: string,
  opt?: { target?: TargetContext | string; noopener?: boolean; noreferrer?: boolean }
) {
  const { target = '__blank', noopener = true, noreferrer = true } = opt || {}
  const feature: string[] = []

  noopener && feature.push('noopener=yes')
  noreferrer && feature.push('noreferrer=yes')

  window.open(url, target, feature.join(','))
}

export function getRawRoute(route: RouteLocationNormalized): RouteLocationNormalized {
  if (!route) {
    return route
  }

  const { matched, ...opt } = route
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path
        }))
      : undefined) as RouteRecordNormalized[]
  }
}

/**
 * js获取唯一值
 * @param len 返回的字符串长度
 * @param radix 类型如 二进制、十进制、十六进制
 * @return string
 * */
export function getUuid(len: number, radix: number) {
  const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.split('')
  const uuid = []
  let i
  radix = radix || chars.length

  if (len) {
    // Compact form
    for (i = 0; i < len; i++) uuid[i] = chars[0 | (Math.random() * radix)]
  } else {
    // rfc4122, version 4 form
    let r

    // rfc4122 requires these characters
    uuid[8] = uuid[13] = uuid[18] = uuid[23] = '-'
    uuid[14] = '4'

    // Fill in random data. At i==19 set the high bits of clock sequence as
    // per rfc4122, sec. 4.1.5
    for (i = 0; i < 36; i++) {
      if (!uuid[i]) {
        r = 0 | (Math.random() * 16)
        uuid[i] = chars[i === 19 ? (r & 0x3) | 0x8 : r]
      }
    }
  }
  return uuid.join('')
}

// 路由跳转
export const goto = (name: RouteRecordName, query?: LocationQueryRaw) => {
  router.push({
    name,
    query: {
      ...query
    }
  })
}

/**
 * 获取文件名的后缀
 * @param { string } fileName 文件名
 * @returns 文件的后缀名
 */
export function getFileExtension(fileName = '') {
  return fileName.split('.').pop()?.toLowerCase()
}

export function isDev() {
  return import.meta.env.DEV || import.meta.env.VITE_BUILD_ENV === 'staging'
}

/**
 * 读取source更新target对象
 * - 不会新增key
 * - 不改变源数据
 * @param target 目标对象
 * @param source 数据来源对象
 * @returns 更新后的数据对象（浅复制）
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function updateObject<T>(target: T, source: any): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const output: any = { ...target }
  Object.keys(target as object).forEach((key) => {
    if (key in source) {
      output[key] = source[key]
    }
  })
  return output as T
}

/**
 * 检索选项
 * @param options 选项列表
 * @param value 检索值
 * @param key 检索`key`
 */
export function getOption<T extends Obj>(
  options: readonly T[] | T[],
  value: any,
  key: keyof T = 'value'
) {
  return options.find((item) => value === item[key])
}

/**
 * 在`options`中检索`[key]=value`的项的`label`属性
 * @param {any} value 检索值
 * @param {{ value: any, label: string }[]} options 列表
 * @param {string} key 检索key
 */
export function getOptionLabel<T extends Obj>(
  value: any,
  options: readonly T[] | T[],
  key = 'value',
  labelKey = 'label'
) {
  const found = getOption(options, value, key)
  if (!found) return ''
  return found[labelKey]
}

/**
 *
 * @param url 需要解析的url
 * @returns 键值对形式的queryString
 */
export function getParams(url: string): Obj {
  const urlArr = url.split('?')
  const queryString: string | undefined = urlArr[1] || urlArr[0]
  const res: Obj = {}
  if (queryString) {
    const queryParams: string[] = queryString.split('&')
    for (const item of queryParams) {
      const keyIndex: number = item.indexOf('=')
      const key: string = item.substring(0, keyIndex)
      const value: string = item.substring(keyIndex + 1)
      res[key] = value
    }
  }
  return res
}

/**
 * 睡眠函数
 * @param {number} ms 毫秒
 * @returns Promise<Boolean>
 */
export function sleep(ms = 0) {
  return new Promise((resovle) => {
    setTimeout(() => {
      resovle(true)
    }, ms)
  })
}

/**
 * 数字转化为千分位货币符号
 *
 * @param {string} val
 * @returns {string} val
 * @example
 *
 * toZhCN('6573');
 * // => ¥6,573.00
 */
export function toZhCN(money: string | number, decimal = 2) {
  if (!money) {
    money = 0
  }
  const result = parseFloat(String(money)).toLocaleString('zh-CN', {
    style: 'currency',
    currency: 'CNY',
    currencyDisplay: 'symbol',
    maximumFractionDigits: decimal,
    minimumFractionDigits: decimal
  })
  return result
}

/**
 * @description 数字转化为千分位
 *
 * @param {number|string} value
 * @param {number} fractionDigits 保留的小数位
 * @return {string}
 */
export function toLocaleStringLike(value: number | string, fractionDigits = 2) {
  const valueParsed = parseFloat(String(value))
  const options = {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  }
  return isNaN(valueParsed) ? value : valueParsed.toLocaleString('zh-CN', options)
}
